<template>
    <div v-if="!hasConsent" class="cookie-consent">
        <div class="cookie-content">
            <div class="cookie-text">
                <p>{{ $t('cookies.message') }}</p>
            </div>
            <div class="cookie-actions">
                <button class="accept-btn" @click="acceptCookies">{{ $t('cookies.accept') }}</button>
                <button class="reject-btn" @click="rejectCookies">{{ $t('cookies.rejectAll') }}</button>
                <button class="settings-btn" @click="openSettings">{{ $t('cookies.settings') }}</button>
            </div>
        </div>
    </div>

    <div v-if="showSettings" class="cookie-settings-overlay" @click="showSettings = false">
        <div class="cookie-settings-modal" @click.stop>
            <h3>{{ $t('cookies.settings') }}</h3>
            <div class="settings-content">
                <div class="cookie-option">
                    <div class="option-text">
                        <div class="option-header">
                            <h4>{{ $t('cookies.essential') }}</h4>
                        </div>
                        <p>{{ $t('cookies.essentialDescription') }}</p>
                    </div>
                    <ToggleComponent
                        v-model="essentialEnabled"
                        :locked="true"
                        class="option-toggle"
                    />
                </div>

                <div class="cookie-option">
                    <div class="option-text">
                        <div class="option-header">
                            <h4>{{ $t('cookies.analytics') }}</h4>
                        </div>
                        <p>{{ $t('cookies.analyticsDescription') }}</p>
                    </div>
                    <ToggleComponent
                        v-model="analyticsEnabled"
                        :locked="false"
                        class="option-toggle"
                    />
                </div>
            </div>
            <div class="settings-actions">
                <button class="save-btn" @click="saveSettings">{{ $t('cookies.savePreferences') }}</button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Cookies from 'js-cookie';
import ToggleComponent from './BaseComponents/ToggleComponent.vue';

const hasConsent = ref(false);
const showSettings = ref(false);
const analyticsEnabled = ref(true);
const essentialEnabled = ref(true);

onMounted(() => {
    const consent = Cookies.get('cookie-consent');
    if (consent) {
        hasConsent.value = true;
        analyticsEnabled.value = Cookies.get('analytics-enabled') === 'true';
    }
});

function acceptCookies() {
    Cookies.set('cookie-consent', 'true', { expires: 365 });
    Cookies.set('analytics-enabled', 'true', { expires: 365 });
    hasConsent.value = true;
}

function rejectCookies() {
    Cookies.set('cookie-consent', 'true', { expires: 365 });
    Cookies.set('analytics-enabled', 'false', { expires: 365 });
    analyticsEnabled.value = false;
    hasConsent.value = true;
}

function openSettings() {
    showSettings.value = true;
}

function saveSettings() {
    Cookies.set('cookie-consent', 'true', { expires: 365 });
    Cookies.set('analytics-enabled', analyticsEnabled.value, { expires: 365 });
    hasConsent.value = true;
    showSettings.value = false;
}
</script>

<style scoped>
.cookie-consent {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: var(--bg-cookie-consent);
    backdrop-filter: var(--backdrop-blur);
    z-index: var(--z-index-cookie-consent);
    padding: var(--spacing-xl);
    box-shadow: var(--shadow-cookie-consent);
    border-top: 1px solid var(--color-white-10);
}

.cookie-content {
    max-width: var(--max-width-cookie-content);
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-3xl);
}

.cookie-text {
    color: var(--color-white-90);
    font-size: var(--font-size-body-sm);
    line-height: var(--line-height-relaxed);
}

.cookie-actions {
    display: flex;
    gap: var(--spacing-xl);
    flex-shrink: 0;
    justify-content: flex-end;
    margin-left: auto;
}

.accept-btn, .reject-btn, .settings-btn {
    padding: var(--padding-btn-md);
    border-radius: var(--border-radius-md);
    border: none;
    cursor: pointer;
    font-size: var(--font-size-caption);
    transition: all var(--transition-normal) ease;
}

.accept-btn {
    background: var(--gradient-success);
    color: var(--color-white);
}

.accept-btn:hover {
    opacity: 0.9;
}

.reject-btn {
    background: var(--color-white-10);
    color: var(--color-white-90);
}

.reject-btn:hover {
    background: var(--color-white-20);
}

.settings-btn {
    background: var(--color-white-10);
    color: var(--color-white-70);
}

.settings-btn:hover {
    background: var(--color-white-20);
}

.cookie-settings-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--bg-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: var(--z-index-cookie-modal);
    backdrop-filter: var(--backdrop-blur-sm);
}

.cookie-settings-modal {
    background: var(--bg-cookie-modal);
    padding: var(--padding-modal);
    border-radius: var(--border-radius-xl);
    width: 90%;
    max-width: var(--max-width-cookie-modal);
}

.cookie-settings-modal h3 {
    margin: 0 0 var(--spacing-2xl) 0;
    color: var(--color-white-90);
}

.settings-content {
    margin-bottom: var(--spacing-3xl);
}

.cookie-option {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    column-gap: var(--spacing-3xl);
    margin-bottom: var(--spacing-2xl);
}

.option-header {
    margin-bottom: var(--spacing-md);
}

.option-header h4 {
    margin: 0;
    color: var(--color-white-80);
}

.option-text {
    min-width: 0;
}

.option-toggle {
    justify-self: end;
    align-self: start;
}

.cookie-option p {
    color: var(--color-white-60);
    font-size: var(--font-size-body-sm);
    margin: 0;
}

.settings-actions {
    display: flex;
    justify-content: flex-end;
}

.save-btn {
    padding: var(--padding-btn-lg);
    border-radius: var(--border-radius-md);
    border: none;
    background: var(--gradient-success);
    color: var(--color-white);
    cursor: pointer;
    font-size: var(--font-size-caption);
    transition: opacity var(--transition-normal);
}

.save-btn:hover {
    opacity: 0.9;
}

@media (max-width: 768px) {
    .cookie-content {
        flex-direction: column;
        text-align: center;
    }

    .cookie-actions {
        width: 100%;
        justify-content: center;
    }
}
</style> 
